// Activates the mobile design layer.
//
// The storefront, the seller portal and the admin dashboards are one React SPA
// shared by three surfaces and two form factors. Rather than fork the app, the
// phone experience is a *layer*: `mobile.css` restyles the existing markup into
// a native-feeling app (its own type scale, spacing, app bar, sheets and list
// patterns) and is gated entirely on a class this module puts on <html>.
//
// Two classes, two different questions:
//
//   .m-app     "should this render as the mobile app?"  — true inside the
//              Capacitor shell, and true in any browser narrower than
//              MOBILE_MAX. Everything in mobile.css hangs off this.
//
//              There is deliberately no "force mobile" flag. This layer only
//              restyles; the structural collapsing (desktop navbar out,
//              sidebar out, grid to two columns) is done by App.css's own
//              `@media (max-width: 768px)` rules, and a media query keys off
//              the real viewport — no class can fake it. Forcing .m-app in a
//              wide window therefore produces a hybrid that is not what any
//              phone shows. To preview on a desktop, give the page a real
//              narrow viewport: devtools device mode, or /preview.html, which
//              frames the app in a 390px iframe.
//   .m-native  "are we actually in the packaged app?"   — only true under
//              Capacitor. Reserved for what needs a Capacitor plugin or is
//              wrong in any browser: claiming the status-bar inset.
//
//   .m-installed
//              "is this running as an installed app?"   — true under
//              Capacitor AND in an installed PWA (display-mode: standalone),
//              whenever .m-app is also on. This is the one that suppresses
//              web-only chrome: the announcement bar and the site footer.
//
//              It exists because the PWA is the same React app launched from
//              a home-screen icon with no browser UI around it, and every
//              reason those things are wrong in the packaged app is equally
//              true there. Gated on .m-native alone, an installed PWA kept
//              the announcement bar and the whole five-column website footer,
//              which is what made it look like the website rather than the
//              app.
//
//              The `.m-app` half of the condition is not incidental. A PWA
//              installed on a desktop opens a wide window with no bottom nav,
//              so the footer is its only navigation — hiding it there would
//              strand the user. Capacitor is always .m-app (see evaluate
//              below), so this changes nothing for the packaged app.
//
// Applied synchronously at import time, before React's first render, so the
// phone never paints a frame of desktop layout before switching.

// Matches the breakpoint the existing bottom nav already switches at
// (BottomNav.css), so navigation and design layer flip together — a phone can
// never end up with the mobile nav but desktop typography, or vice versa.
export const MOBILE_MAX = 768;

const MOBILE_QUERY = `(max-width: ${MOBILE_MAX}px)`;

// Capacitor is read defensively rather than imported: this module runs before
// React mounts, and on the web `@capacitor/core` resolves to a stub whose
// isNativePlatform() is false. Any failure here must degrade to "web", never
// throw, or the whole app fails to boot.
function detectNative() {
  try {
    const cap = window.Capacitor;
    if (!cap) return false;
    return typeof cap.isNativePlatform === 'function'
      ? cap.isNativePlatform()
      : Boolean(cap.isNative);
  } catch {
    return false;
  }
}

// Launched from a home-screen icon rather than in a browser tab.
//
// `display-mode: standalone` is the Android/desktop signal; navigator.standalone
// is iOS Safari's own, which predates it and is still the only one there. Same
// pair usePwaInstall.js uses to decide the install offer has been taken up, so
// the two cannot disagree about whether the app is installed.
//
// Defensive for the same reason as detectNative(): this runs before React
// mounts, and anything that throws here stops the app booting at all.
function detectStandalone() {
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    return window.navigator?.standalone === true;
  } catch {
    return false;
  }
}

// Which surface this build is running on, for code that needs to *record* the
// platform rather than style for it -- currently the sales channel stamped on
// an order at checkout.
//
// Deliberately not the same question as isMobileUI(): a narrow browser window
// is mobile UI but is still the website. Only Capacitor is 'app'. Reuses
// detectNative() above, so the answer cannot drift from the .m-native class.
export function getSalesChannel() {
  return detectNative() ? 'app' : 'web';
}

function apply(root, isMobile, isNative, isStandalone) {
  root.classList.toggle('m-app', isMobile);
  root.classList.toggle('m-native', isNative);
  root.classList.toggle('m-installed', isMobile && (isNative || isStandalone));
}

// Condensed-header scrolling.
//
// At rest the storefront header shows the logo row above the search field.
// Once the page has scrolled past the logo, the logo row folds away and the
// search field becomes the sticky header on its own -- so the thing a
// shopper reaches for mid-scroll is a search box, not a brand mark they have
// already seen.
//
// Two thresholds, not one. A single threshold makes the header flicker when
// a scroll settles exactly on it: the collapse changes the page height,
// which can nudge the scroll back across the line, which expands it again.
// Collapsing at 72px and only re-expanding below 24px gives it somewhere to
// rest.
const CONDENSE_AT = 72;
const EXPAND_BELOW = 24;

function initCondensedHeader(root) {
  let condensed = false;
  let ticking = false;

  const apply = () => {
    ticking = false;
    // Mobile only. Checked here rather than once at setup because .m-app is
    // viewport-driven: a desktop window dragged narrow (or a rotation) has to
    // start condensing, and dragged wide again has to stop. Without this the
    // class was applied at any width and the DESKTOP logo folded away on
    // scroll -- a regression in the web app this layer must never touch.
    if (!root.classList.contains('m-app')) {
      if (condensed) { condensed = false; root.classList.remove('m-condensed'); }
      return;
    }
    const y = window.scrollY || 0;
    if (!condensed && y > CONDENSE_AT) {
      condensed = true;
      root.classList.add('m-condensed');
    } else if (condensed && y < EXPAND_BELOW) {
      condensed = false;
      root.classList.remove('m-condensed');
    }
  };

  const onScroll = () => {
    // Coalesce to one class check per frame; scroll fires far more often than
    // the header can usefully change.
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  apply();
}

export function initMobileUI() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const root = document.documentElement;
  const isNative = detectNative();
  const mql = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;

  // Native is always the mobile app regardless of reported width — a tablet in
  // the Capacitor shell is still a touch device with a bottom nav, and the
  // desktop layout has no way to be reached there.
  const evaluate = () => apply(
    root,
    isNative || (mql ? mql.matches : false),
    isNative,
    detectStandalone(),
  );

  evaluate();
  initCondensedHeader(root);

  // Standalone is re-read on every evaluate rather than captured once: a PWA
  // can be installed while a tab is open, and that tab's display-mode flips
  // without a reload.
  const standaloneMql = window.matchMedia ? window.matchMedia('(display-mode: standalone)') : null;
  if (standaloneMql?.addEventListener) standaloneMql.addEventListener('change', evaluate);

  if (!mql) return;
  // Keeps the layer honest through rotation, split-screen, and someone
  // dragging a desktop window narrow — the same signal the CSS itself uses.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', evaluate);
  } else if (typeof mql.addListener === 'function') {
    mql.addListener(evaluate); // Safari < 14
  }
}

// True when the mobile layer is currently active. For the rare case where a
// component must branch in JS (rendering a sheet instead of a modal) rather
// than restyle in CSS. Read live so it stays correct after a resize.
export function isMobileUI() {
  return typeof document !== 'undefined'
    && document.documentElement.classList.contains('m-app');
}

// True when this is running as an installed app -- the packaged Capacitor
// build or a PWA opened from the home screen -- and rendering at phone size.
//
// The JS counterpart to the .m-installed class, and the thing a component
// should ask before dropping web-only chrome. Deliberately NOT the same
// question as isNativePlatform(): anything that needs a Capacitor plugin, or
// that would be a security hole in a browser (config.js's API override), must
// keep asking that one instead.
//
// Reads the class rather than recomputing, so there is exactly one answer on
// the page and it cannot drift from what the CSS is doing.
export function isInstalledApp() {
  return typeof document !== 'undefined'
    && document.documentElement.classList.contains('m-installed');
}
